import React, { useEffect, useState } from "react";
import { collection, getDocs, doc, updateDoc, deleteDoc } from "firebase/firestore";
import { db } from "./firebase";
import TutorLayout from "./TutorLayout";

function AllBookings() {
  const [bookings, setBookings] = useState([]);
  const [studentsMap, setStudentsMap] = useState({});
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [message, setMessage] = useState("");
  const [showMoreIds, setShowMoreIds] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      const bookingsSnap = await getDocs(collection(db, "bookings"));
      const usersSnap = await getDocs(collection(db, "users"));

      const usersMap = {};
      usersSnap.docs.forEach((doc) => {
        const user = doc.data();
        usersMap[doc.id] = `${user.displayName || ""} ${user.surname || ""}`.trim();
      });

      const allBookings = bookingsSnap.docs
        .map((doc) => ({ id: doc.id, ...doc.data() }))
        .sort((a, b) => new Date(`${a.date}T${a.time}`) - new Date(`${b.date}T${b.time}`));

      setBookings(allBookings);
      setStudentsMap(usersMap);
    };

    fetchData();
  }, []);

  const formatDate = (dateStr) => {
    const [y, m, d] = dateStr.split("-");
    return `${d}/${m}/${y}`;
  };

  const handleEdit = (booking) => {
    setEditingId(booking.id);
    setEditForm({ ...booking });
    setMessage("");
  };

  const handleChange = (field, value) => {
    setEditForm((prev) => ({ ...prev, [field]: value }));
  };

  const saveChanges = async () => {
    try {
      const bookingRef = doc(db, "bookings", editingId);
      await updateDoc(bookingRef, editForm);
      setMessage("✅ Booking updated.");
      setEditingId(null);
      const updatedList = bookings.map((b) => (b.id === editingId ? { ...editForm, id: editingId } : b));
      setBookings(updatedList);
    } catch (error) {
      console.error(error);
      setMessage("❌ Failed to update booking.");
    }
  };

  const handleDelete = async (id) => {
    const confirm = window.confirm("Are you sure you want to delete this booking?");
    if (!confirm) return;

    try {
      await deleteDoc(doc(db, "bookings", id));
      setBookings((prev) => prev.filter((b) => b.id !== id));
      setMessage("🗑️ Booking deleted.");
    } catch (error) {
      console.error(error);
      setMessage("❌ Failed to delete booking.");
    }
  };

  const now = new Date();
  const pastBookings = bookings.filter(b => new Date(`${b.date}T${b.time}`) < now);
  const upcomingBookings = bookings.filter(b => new Date(`${b.date}T${b.time}`) >= now);

  const renderBooking = (booking, index, totalCount) => (
    <li key={booking.id} style={styles.card}>
      {editingId === booking.id ? (
        <>
          <div>
            <label>Date: </label>
            <input type="date" value={editForm.date} onChange={(e) => handleChange("date", e.target.value)} />
          </div>
          <div>
            <label>Time: </label>
            <input type="time" value={editForm.time} onChange={(e) => handleChange("time", e.target.value)} />
          </div>
          <div>
            <label>Duration: </label>
            <select value={editForm.duration || 60} onChange={(e) => handleChange("duration", e.target.value)}>
              <option value="30">30 minutes</option>
              <option value="45">45 minutes</option>
              <option value="60">1 hour</option>
              <option value="90">1 hour 30 minutes</option>
              <option value="120">2 hours</option>
            </select>
          </div>
          <div>
            <label>Frequency: </label>
            <select value={editForm.frequency} onChange={(e) => handleChange("frequency", e.target.value)}>
              <option value="one-off">One-off</option>
              <option value="weekly">Weekly</option>
              <option value="fortnightly">Fortnightly</option>
            </select>
          </div>
          <div>
            <label>Subject: </label>
            <select value={editForm.subject} onChange={(e) => handleChange("subject", e.target.value)}>
              <option value="GCSE Maths">📐 GCSE Maths</option>
              <option value="A-Level Maths">📐 A-Level Maths</option>
              <option value="GCSE French">🇫🇷 GCSE French</option>
              <option value="A-Level French">🇫🇷 A-Level French</option>
            </select>
          </div>
          <div>
            <label>Notes: </label>
            <textarea value={editForm.notes} onChange={(e) => handleChange("notes", e.target.value)} />
          </div>
          <button onClick={saveChanges} style={styles.saveBtn}>Save</button>
          <button onClick={() => setEditingId(null)} style={styles.cancelBtn}>Cancel</button>
        </>
      ) : (
        <>
          <p><strong>{formatDate(booking.date)}</strong> at <strong>{booking.time}</strong></p>
          <p>👤 {studentsMap[booking.studentId]}</p>
          <p>📚 {booking.subject} • {booking.frequency}</p>
          <p>⏱️ {booking.duration || 60} mins</p>
          {booking.notes && <p>📝 {booking.notes}</p>}
          <button onClick={() => handleEdit(booking)} style={styles.editBtn}>Edit</button>
          <button onClick={() => handleDelete(booking.id)} style={styles.deleteBtn}>Delete</button>
        </>
      )}
    </li>
  );

  return (
    <TutorLayout>
      <div style={styles.container}>
        <h2>📖 All Bookings</h2>
        {message && <p style={styles.message}>{message}</p>}

        <h3>📅 Upcoming Bookings</h3>
        <ul style={{ listStyle: "none", padding: 0 }}>
          {(showMoreIds.includes("upcoming") ? upcomingBookings : upcomingBookings.slice(0, 10)).map((b, idx) =>
            renderBooking(b, idx, upcomingBookings.length)
          )}
        </ul>
        {upcomingBookings.length > 10 && !showMoreIds.includes("upcoming") && (
          <button onClick={() => setShowMoreIds(prev => [...prev, "upcoming"])} style={styles.showMoreBtn}>
            Show More
          </button>
        )}

        <h3 style={{ marginTop: "2rem" }}>📜 Past Bookings</h3>
        <ul style={{ listStyle: "none", padding: 0 }}>
          {pastBookings.map(renderBooking)}
        </ul>
      </div>
    </TutorLayout>
  );
}

const styles = {
  container: {
    maxWidth: "800px",
    margin: "2rem auto",
    padding: "2rem",
    background: "#fff",
    borderRadius: "12px",
    boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
    fontFamily: "'Poppins', sans-serif",
  },
  card: {
    background: "#f8f8f8",
    padding: "1rem",
    marginBottom: "1.2rem",
    borderRadius: "8px",
    boxShadow: "0 2px 5px rgba(0,0,0,0.05)",
  },
  message: {
    marginBottom: "1rem",
    color: "#155724",
    backgroundColor: "#d4edda",
    padding: "0.75rem",
    borderRadius: "6px",
  },
  editBtn: {
    backgroundColor: "#007bff",
    color: "white",
    border: "none",
    padding: "0.5rem 1rem",
    borderRadius: "6px",
    marginRight: "0.5rem",
    cursor: "pointer",
  },
  deleteBtn: {
    backgroundColor: "#dc3545",
    color: "white",
    border: "none",
    padding: "0.5rem 1rem",
    borderRadius: "6px",
    cursor: "pointer",
  },
  saveBtn: {
    backgroundColor: "#28a745",
    color: "white",
    padding: "0.5rem 1rem",
    border: "none",
    borderRadius: "6px",
    marginTop: "0.5rem",
  },
  cancelBtn: {
    backgroundColor: "#ccc",
    color: "#333",
    padding: "0.5rem 1rem",
    border: "none",
    borderRadius: "6px",
    marginTop: "0.5rem",
    marginLeft: "0.5rem",
  },
  showMoreBtn: {
    backgroundColor: "#eee",
    color: "#333",
    padding: "0.5rem 1rem",
    border: "1px solid #ccc",
    borderRadius: "6px",
    cursor: "pointer",
  },
};

export default AllBookings;
